import { supabase } from "@/integrations/supabase/client";

export type Platform = 'twitter' | 'instagram' | 'linkedin' | 'facebook' | 'threads';

export interface PlatformLimits {
  maxCharacters: number;
  maxImages: number;
  maxVideos: number;
  requiresMedia: boolean;
  supportsCarousel: boolean;
}

export interface PublishResult {
  success: boolean;
  platformPostId?: string;
  platformPostUrl?: string;
  error?: string;
}

export class PlatformPublisher {
  private static readonly PLATFORM_LIMITS: Record<Platform, PlatformLimits> = {
    twitter: {
      maxCharacters: 280,
      maxImages: 4,
      maxVideos: 1,
      requiresMedia: false,
      supportsCarousel: true,
    },
    instagram: {
      maxCharacters: 2200,
      maxImages: 10,
      maxVideos: 1,
      requiresMedia: true,
      supportsCarousel: true,
    },
    linkedin: {
      maxCharacters: 3000,
      maxImages: 9,
      maxVideos: 1,
      requiresMedia: false,
      supportsCarousel: true,
    },
    facebook: {
      maxCharacters: 63206,
      maxImages: 10,
      maxVideos: 1,
      requiresMedia: false,
      supportsCarousel: true,
    },
    threads: {
      maxCharacters: 500,
      maxImages: 10,
      maxVideos: 1,
      requiresMedia: false,
      supportsCarousel: true,
    },
  };

  static getLimits(platform: Platform): PlatformLimits {
    return this.PLATFORM_LIMITS[platform];
  }

  static getCharacterLimit(platform: Platform): number {
    return this.PLATFORM_LIMITS[platform].maxCharacters;
  }

  static validatePost(
    platform: Platform,
    content: string,
    mediaUrls: string[]
  ): { valid: boolean; error?: string } {
    const limits = this.getLimits(platform);

    // Character limit check
    if (content.length > limits.maxCharacters) {
      return {
        valid: false,
        error: `Content exceeds ${limits.maxCharacters} character limit for ${platform}`,
      };
    }

    // Media requirement check
    if (limits.requiresMedia && mediaUrls.length === 0) {
      return {
        valid: false,
        error: `${platform} requires at least one image or video`,
      };
    }

    // Image count check
    const imageCount = mediaUrls.filter(url => 
      url.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    ).length;
    
    if (imageCount > limits.maxImages) {
      return {
        valid: false,
        error: `Maximum ${limits.maxImages} images allowed for ${platform}`,
      };
    }

    // Video count check
    const videoCount = mediaUrls.filter(url => 
      url.match(/\.(mp4|mov)$/i)
    ).length;
    
    if (videoCount > limits.maxVideos) {
      return {
        valid: false,
        error: `Maximum ${limits.maxVideos} video allowed for ${platform}`,
      };
    }

    return { valid: true };
  }

  static async publish(
    postId: string,
    content: string,
    mediaUrls: string[],
    socialAccountId: string
  ): Promise<PublishResult> {
    try {
      // Get the social account to determine platform
      const { data: account, error: accountError } = await supabase
        .from('social_accounts')
        .select('platform')
        .eq('id', socialAccountId)
        .single();

      if (accountError || !account) {
        return {
          success: false,
          error: 'Social account not found',
        };
      }

      const platform = account.platform as Platform;

      // Validate the post
      const validation = this.validatePost(platform, content, mediaUrls);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
        };
      }

      // Call the appropriate publishing edge function
      const { data, error } = await supabase.functions.invoke(
        `publish-to-${platform}`,
        {
          body: {
            postId,
            content,
            mediaUrls,
            socialAccountId,
          },
        }
      );

      if (error) {
        return {
          success: false,
          error: error.message || 'Failed to publish post',
        };
      }

      return {
        success: true,
        platformPostId: data?.platformPostId,
        platformPostUrl: data?.platformPostUrl,
      };
    } catch (error) {
      console.error('Publishing error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  static getCharacterWarningThreshold(platform: Platform): number {
    const limit = this.getCharacterLimit(platform);
    return Math.floor(limit * 0.9); // 90% of limit
  }

  static extractHashtags(content: string): string[] {
    const hashtagRegex = /#[\w]+/g;
    return content.match(hashtagRegex) || [];
  }

  static countHashtags(content: string): number {
    return this.extractHashtags(content).length;
  }
}
